import {
  COTE_ENVOI_MIN_PX,
  COTE_ENVOI_OPAQUE_PX,
  COTE_ENVOI_PX,
  IMAGE_ENVOI_OCTETS_MAX,
  IMAGE_SOURCE_OCTETS_MAX,
} from "./contract";
import { nombreFr } from "./silhouettes-textes";

/**
 * La reduction de l'image dans le navigateur, avant l'envoi.
 *
 * Le frontal nginx du template refuse un corps de plus de 2 Mo et ne se regle pas depuis
 * l'app : l'image part donc en 1 450 000 octets au plus. Une image de 800 px au plus sur son
 * grand cote et legere part telle quelle, sans rien lire d'elle ; `products` applique
 * l'orientation EXIF, un JPEG d'appareil ne sort donc pas couche.
 *
 * Au-dela, sa transparence decide, par la regle de
 * `packages/products/silhouette/silhouette/masque.py` : si elle sert de detourage (pixels
 * d'alpha inferieur a 128 sur la moitie du cadre au moins, ou sur 10 % de l'image), l'image
 * part en PNG, qui la garde, et 1 200 px au plus : telle quelle si elle y tient et reste
 * legere. Sinon elle est opaque, c'est l'outil qui la detoure, et c'est la que le calcul coute :
 * elle part en JPEG 0,92, peinte sur fond blanc d'abord comme `rgb_sur_fond_blanc` le fait
 * dans l'outil, et 800 px au plus. Un PNG opaque et detaille (les dessins de ChatGPT) restait
 * trop lourd en PNG meme reduit, et trop lent a 1 024 px (relecture de #145).
 *
 * Quand l'export reste trop lourd, les paliers descendent : 80 % du precedent tant qu'il reste
 * 600 px au moins.
 *
 * jsdom n'a ni canevas ni `createImageBitmap` : `silhouettes-image.test.ts` les remplace pour
 * suivre les regles, et la vraie reduction est prouvee par l'e2e, dans Chromium. Les tests de
 * la page, eux, remplacent `reduireImage` entiere par `vi.mock`.
 */

/** Une image refusee avant l'envoi : le message se lit tel quel, dans la carte Image. */
export class ErreurImage extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ErreurImage";
  }
}

export type ImageReduite = {
  /** Les octets envoyes, en base64, sans prefixe `data:`. */
  base64: string;
  /** Les dimensions de l'image choisie, en pixels, avant reduction. */
  largeur: number;
  hauteur: number;
  /** La taille du fichier choisi. */
  octets: number;
  /** Vrai quand l'image envoyee a ete redessinee plus petite. */
  reduite: boolean;
};

const FORMATS = ["image/png", "image/jpeg", "image/webp"];
const QUALITE_JPEG = 0.92;

/** Chaque palier fait 80 % du precedent. */
const FACTEUR_PALIER = 0.8;

/** La regle de `masque.py` : sous cet alpha, un pixel est transparent (`alpha_min`). */
const ALPHA_MIN = 128;
/** Part du cadre transparente pour que le canal alpha serve (`CADRE_TRANSPARENT_MIN`). */
const CADRE_TRANSPARENT_MIN = 0.5;
/** Ou part de l'image entiere (`SURFACE_TRANSPARENTE_MIN`). */
const SURFACE_TRANSPARENTE_MIN = 0.1;

/**
 * Les grands cotes essayes, strictement decroissants : min(grand cote, `plafond`), puis 80 %
 * du precedent, arrondi, tant qu'il reste 600 px au moins. Le plafond vaut 1 200 px pour une
 * image detouree, 800 pour une opaque.
 */
export function paliers(grandCote: number, plafond: number = COTE_ENVOI_PX): number[] {
  const liste = [Math.min(grandCote, plafond)];
  for (;;) {
    const precedent = liste[liste.length - 1] ?? 0;
    const suivant = Math.round(precedent * FACTEUR_PALIER);
    if (suivant < COTE_ENVOI_MIN_PX || suivant >= precedent) return liste;
    liste.push(suivant);
  }
}

/**
 * Vrai quand la transparence de l'image sert de detourage, par la regle de `masque.py` : des
 * pixels transparents, et au moins la moitie du cadre (premiere et derniere lignes, premiere
 * et derniere colonnes, comptees comme `numpy.concatenate` le fait) ou 10 % de l'image.
 */
export function transparenceSert(
  pixels: Uint8ClampedArray,
  largeur: number,
  hauteur: number,
): boolean {
  const transparent = (x: number, y: number): boolean =>
    (pixels[(y * largeur + x) * 4 + 3] ?? 255) < ALPHA_MIN;
  let transparents = 0;
  for (let y = 0; y < hauteur; y += 1) {
    for (let x = 0; x < largeur; x += 1) {
      if (transparent(x, y)) transparents += 1;
    }
  }
  if (transparents === 0) return false;
  let cadre = 0;
  for (let x = 0; x < largeur; x += 1) {
    if (transparent(x, 0)) cadre += 1;
    if (transparent(x, hauteur - 1)) cadre += 1;
  }
  for (let y = 0; y < hauteur; y += 1) {
    if (transparent(0, y)) cadre += 1;
    if (transparent(largeur - 1, y)) cadre += 1;
  }
  const bord = 2 * largeur + 2 * hauteur;
  return (
    cadre / bord >= CADRE_TRANSPARENT_MIN ||
    transparents / (largeur * hauteur) >= SURFACE_TRANSPARENTE_MIN
  );
}

/** Le base64 d'un fichier ou d'un export de canevas, sans son prefixe `data:...;base64,`. */
function base64De(blob: Blob): Promise<string> {
  return new Promise((resoudre, rejeter) => {
    const lecteur = new FileReader();
    lecteur.onload = () => {
      const url = String(lecteur.result);
      resoudre(url.slice(url.indexOf(",") + 1));
    };
    lecteur.onerror = () => rejeter(lecteur.error ?? new Error("lecture impossible"));
    lecteur.readAsDataURL(blob);
  });
}

type Format = "image/png" | "image/jpeg";

type Dessin = { toile: HTMLCanvasElement; pinceau: CanvasRenderingContext2D };

/**
 * Redessine l'image a `cote` pixels sur son grand cote. En JPEG, le fond est peint en blanc
 * avant l'image, faute de quoi la transparence sortirait noire. `lire` previent le navigateur
 * que les pixels seront relus.
 */
function dessiner(image: ImageBitmap, cote: number, format: Format, lire = false): Dessin | null {
  const echelle = cote / Math.max(image.width, image.height);
  const toile = document.createElement("canvas");
  toile.width = Math.max(1, Math.round(image.width * echelle));
  toile.height = Math.max(1, Math.round(image.height * echelle));
  const pinceau = toile.getContext("2d", { willReadFrequently: lire });
  if (!pinceau) return null;
  pinceau.imageSmoothingQuality = "high";
  if (format === "image/jpeg") {
    pinceau.fillStyle = "#ffffff";
    pinceau.fillRect(0, 0, toile.width, toile.height);
  }
  pinceau.drawImage(image, 0, 0, toile.width, toile.height);
  return { toile, pinceau };
}

function exporter(toile: HTMLCanvasElement, format: Format): Promise<Blob | null> {
  return new Promise<Blob | null>((resoudre) =>
    toile.toBlob(resoudre, format, format === "image/jpeg" ? QUALITE_JPEG : undefined),
  );
}

/**
 * Le premier export assez leger, en descendant les paliers de `cotes` ; `null` quand aucun ne
 * l'est. `deja`, s'il est donne, est le dessin du premier palier : il n'est pas refait.
 */
async function premierExportLeger(
  image: ImageBitmap,
  cotes: number[],
  format: Format,
  deja: Dessin | null,
): Promise<Blob | null> {
  for (const [rang, cote] of cotes.entries()) {
    const dessin = rang === 0 && deja ? deja : dessiner(image, cote, format);
    if (!dessin) return null;
    const export_ = await exporter(dessin.toile, format);
    if (export_ && export_.size <= IMAGE_ENVOI_OCTETS_MAX) return export_;
  }
  return null;
}

/**
 * Prepare l'image choisie pour l'envoi, dans l'ordre : le format, le poids du fichier, le
 * decodage, puis, au-dela de 800 px ou trop lourde, la lecture de sa transparence et la
 * reduction par paliers.
 */
export async function reduireImage(fichier: File): Promise<ImageReduite> {
  if (!FORMATS.includes(fichier.type)) {
    throw new ErreurImage(`${fichier.name} n'est pas une image PNG, JPEG ou WebP.`);
  }
  if (fichier.size > IMAGE_SOURCE_OCTETS_MAX) {
    const mo = nombreFr(fichier.size / (1024 * 1024), 1);
    throw new ErreurImage(
      `${fichier.name} fait ${mo} Mo : 10 Mo au plus. Exporte-la en PNG plus petit, ou en JPEG.`,
    );
  }

  let image: ImageBitmap;
  try {
    // L'orientation EXIF est appliquee ici (`imageOrientation: "from-image"`, le defaut).
    image = await createImageBitmap(fichier);
  } catch {
    throw new ErreurImage(`${fichier.name} n'est pas une image PNG, JPEG ou WebP.`);
  }

  try {
    const largeur = image.width;
    const hauteur = image.height;
    const decrite = { largeur, hauteur, octets: fichier.size };
    const telleQuelle = async (): Promise<ImageReduite> => ({
      ...decrite,
      base64: await base64De(fichier),
      reduite: false,
    });
    const grandCote = Math.max(largeur, hauteur);
    const legere = fichier.size <= IMAGE_ENVOI_OCTETS_MAX;
    if (grandCote <= COTE_ENVOI_OPAQUE_PX && legere) return telleQuelle();

    // La transparence se lit sur l'image telle qu'elle partirait en PNG, a son premier palier.
    const analyse = dessiner(image, Math.min(grandCote, COTE_ENVOI_PX), "image/png", true);
    let export_: Blob | null = null;
    if (analyse) {
      const { width, height } = analyse.toile;
      const { data } = analyse.pinceau.getImageData(0, 0, width, height);
      if (transparenceSert(data, width, height)) {
        if (grandCote <= COTE_ENVOI_PX && legere) return telleQuelle();
        export_ = await premierExportLeger(image, paliers(grandCote), "image/png", analyse);
      } else {
        export_ = await premierExportLeger(
          image,
          paliers(grandCote, COTE_ENVOI_OPAQUE_PX),
          "image/jpeg",
          null,
        );
      }
    }
    if (export_) return { ...decrite, base64: await base64De(export_), reduite: true };
    throw new ErreurImage(
      `${fichier.name} reste trop lourde même réduite : exporte-la plus petite.`,
    );
  } finally {
    image.close();
  }
}
